import {
  CreateCompany,
  DeleteCompany,
  DeleteLogoCompany,
  ListCompanies,
  ShowCompany,
  UpdateCompany,
  UpdateCompanyLogo,
} from '@/services/companyService';
import { Company, CreateCompanyForm } from '@/types/company';
import { Query } from '@/types/query';
import { Response } from '@/types/response';
import { handleError } from '@/utils/handleError';
import toast from 'react-hot-toast';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

interface CompanyState {
  companies: Company[];
  company: Company | null;
  filters: Query;
  loading: boolean;

  listCompanies: (query: Query) => Promise<void>;
  showCompany: (id: number) => Promise<void>;
  createCompany: (company: CreateCompanyForm) => Promise<Response<Company>>;
  updateCompany: (company: Company) => Promise<void>;
  deleteCompany: (id: number) => Promise<void>;
  updateCompanyLogo: (company_id: number, logo: string) => Promise<void>;
  deleteCompanyLogo: (company_id: number) => Promise<void>;
  setFilters: (filters: Partial<Query>) => void;
}

export const useCompanyStore = create<CompanyState>()(
  immer<CompanyState>(set => ({
    companies: [],
    company: null,
    filters: {
      paginate: undefined,
      current_page: 1,
      per_page: 10,
      total: 0,
      last_page: 1,
      sort_field: 'id',
      sort_order: 'desc',
      search: '',
    },
    loading: false,

    setFilters: (filters: Partial<Query>) => {
      set(state => {
        Object.assign(state.filters, filters);
      });
    },

    listCompanies: async (query: Query) => {
      try {
        set(state => {
          state.loading = true;
        });
        const { data } = await ListCompanies(query);

        if ('pagination' in data) {
          const paginatedData = data as unknown as {
            data: Company[];
            pagination: {
              per_page: number;
              current_page: number;
              last_page: number;
              total: number;
            };
          };

          set(state => {
            state.companies =
              query.current_page === 1
                ? paginatedData.data
                : [...state.companies, ...paginatedData.data];

            state.filters = {
              ...state.filters,
              per_page: paginatedData.pagination.per_page,
              current_page: paginatedData.pagination.current_page,
              last_page: paginatedData.pagination.last_page,
              total: paginatedData.pagination.total,
            };
            state.loading = false;
          });
        } else {
          // Non-paginated response
          set(state => {
            state.companies = data as Company[];
            state.filters = {
              ...state.filters,
              total: (data as Company[]).length,
              per_page: (data as Company[]).length,
              current_page: 1,
              last_page: 1,
            };
            state.loading = false;
          });
        }
      } catch (error) {
        handleError(error);
      }
    },

    showCompany: async (id: number) => {
      try {
        set(state => {
          state.loading = true;
        });
        const { data } = await ShowCompany(id);
        set(state => {
          state.company = data;
        });
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    createCompany: async (company: CreateCompanyForm) => {
      try {
        set(state => {
          state.loading = true;
        });
        const { data, message } = await CreateCompany(company);
        toast.success(message);
        set(state => {
          state.company = data;
          state.companies.push(data);
        });
        return { data, message };
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    updateCompany: async (company: Company) => {
      try {
        set(state => {
          state.loading = true;
        });
        const { data, message } = await UpdateCompany(company);
        toast.success(message);
        set(draft => {
          draft.company = data;
          const index = draft.companies.findIndex(c => c.id === data.id);
          if (index !== -1) {
            draft.companies[index] = data;
          }
        });
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    deleteCompany: async (id: number) => {
      try {
        set(state => {
          state.loading = true;
        });
        const { message } = await DeleteCompany(id);
        toast.success(message);
        set(state => {
          state.companies = state.companies.filter(
            company => company.id !== id,
          );
          if (state.company?.id === id) {
            state.company = null;
          }
        });
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    updateCompanyLogo: async (company_id: number, logo: string) => {
      try {
        set(state => {
          state.loading = true;
        });
        const { data, message } = await UpdateCompanyLogo(company_id, logo);
        toast.success(message);
        set(state => {
          if (state.company && state.company.id === company_id) {
            state.company = data;
          }
          const index = state.companies.findIndex(c => c.id === company_id);
          if (index !== -1) {
            state.companies[index] = data;
          }
        });
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    deleteCompanyLogo: async (company_id: number) => {
      try {
        set(state => {
          state.loading = true;
        });
        const { message, data } = await DeleteLogoCompany(company_id);
        toast.success(message);
        set(state => {
          state.company = data;
          const index = state.companies.findIndex(c => c.id === company_id);
          if (index !== -1) {
            state.companies[index] = data;
          }
        });
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },
  })),
);
